import React, { useEffect, useRef, useState } from "react";

/* ─────────────────────────────────────────────
   Scroll-reveal hook
───────────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Data
───────────────────────────────────────────── */
const capabilities = [
  "Concept Design & Engineering",
  "Detailed Technical Planning",
  "Procurement & Global Sourcing",
  "Construction & Installation",
  "Testing & Commissioning",
  "Operations & Maintenance Support",
];

const sectors = [
  { label: "Hospitality & Luxury Hotels", icon: "🏨" },
  { label: "Airports & Infrastructure",   icon: "✈️" },
  { label: "Corporate Offices",           icon: "🏢" },
  { label: "Healthcare & Hospitals",      icon: "🏥" },
  { label: "Telecom Networks",            icon: "📡" },
  { label: "Industrial & Manufacturing",  icon: "🏭" },
  { label: "Retail & Large Format",       icon: "🛍️" },
  { label: "Education & Institutions",    icon: "🎓" },
];

const stats = [
  { number: "18+", label: "Years of Excellence" },
  { number: "35+", label: "Qualified Engineers" },
  { number: "250+", label: "Supervisors & Technicians" },
  { number: "2",   label: "ISO Certifications" },
];

const credentials = [
  { icon: "✦", label: "Quality Management",  value: "ISO 9001:2015 Certified" },
  { icon: "⛑", label: "Occupational Safety", value: "ISO 45001:2018 Certified" },
  { icon: "🏢", label: "MSME / UDYAM",        value: "UDYAM-UP290005217" },
  { icon: "🌐", label: "FIEO RCMC",           value: "NR/664/2020-2021" },
  { icon: "🇺🇸", label: "USA Contractor",     value: "D.U.N.S 650400935 · N-CAGE SFP37" },
  { icon: "⚡", label: "Renewable Energy",    value: "MNRE Registered" },
];

const fontLink =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap";

export default function About() {
  useEffect(() => {
    if (!document.querySelector(`link[href="${fontLink}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fontLink;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div
      className="bg-white text-gray-800 overflow-x-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
    >
      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shineSweep {
          0%        { transform: translateX(-100%); }
          50%, 100% { transform: translateX(220%); }
        }
        .cap-item:hover .cap-bar   { height: 28px; }
        .cap-item:hover .cap-text  { color: #ea580c; }
        .sector-tile:hover         { background: #fff7f0; border-color: #ea580c; }
        .sector-tile:hover .sweep  { width: 100%; }
        .img-wrap:hover img        { transform: scale(1.03); }
        .img-wrap:hover .img-frame { border-color: rgba(234,88,12,0.5); }
        .cred-row:hover            { background: #fff7f0; }
        .hero-bg { background-image: url('/about.png'); }
        @media (max-width: 1299px) { .hero-bg { background-image: url('/about.jpg'); } }
      `}</style>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative min-h-[600px] w-full overflow-hidden">
        <div
          className="hero-bg absolute inset-0 bg-cover bg-center"
          aria-hidden="true"
        />
        {/* Gradient overlays for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-white/15 to-white/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/45 via-white/15 to-transparent" />

        {/* Decorative orange vertical rule */}
        <div className="absolute top-0 left-[38%] h-full w-px bg-gradient-to-b from-transparent via-orange-400/25 to-transparent hidden lg:block" />

        {/* Hero text pinned to bottom-left */}
        <div className="absolute inset-0 flex flex-col justify-end pb-20 px-8 lg:px-20">
          <div
            style={{ animation: "heroFadeUp 0.9s ease 0.2s both" }}
            className="flex items-center gap-3 mb-4"
          >
            <span className="w-8 h-px bg-orange-500" />
            
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 400,
              animation: "heroFadeUp 1s ease 0.4s both",
            }}
            className="text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-gray-900 mb-5"
          >
            Engineering{" "}
            <em className="text-orange-500" style={{ fontStyle: "italic" }}>
              Excellence,
            </em>
            <br />
            Delivered.
          </h1>

          <p
            style={{
              fontWeight: 300,
              maxWidth: 520,
              animation: "heroFadeUp 1s ease 0.6s both",
            }}
            className="text-gray-600 text-[15px] leading-loose"
          >
            India's trusted partner for Electrical, MEP, Civil Finishing &amp;
            Solar EPC — built on precision, integrity, and two decades of
            proven performance.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          STATS BAND
      ══════════════════════════════════════ */}
      <div className="border-y border-orange-100 bg-orange-50/70">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 divide-x divide-orange-100">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="px-10 py-10">
                <div
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                  className="text-5xl text-gray-900 leading-none mb-2"
                >
                  {s.number}
                </div>
                <div
                  className="text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-3"
                  style={{ fontWeight: 500 }}
                >
                  {s.label}
                </div>
                <span className="block w-8 h-[3px] bg-orange-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════
          ABOUT SECTION
      ══════════════════════════════════════ */}
      <section className="py-28 px-6 lg:px-20 max-w-7xl mx-auto">

        {/* Section Title */}
        <Reveal className="text-center mb-16">
          <p
            className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-4 flex items-center justify-center gap-3"
            style={{ fontWeight: 500 }}
          >
            <span className="w-6 h-px bg-orange-400" />
            Who We Are
            <span className="w-6 h-px bg-orange-400" />
          </p>
          <h2
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            className="text-4xl md:text-5xl text-gray-900 mb-5"
          >
            About{" "}
            <em style={{ fontStyle: "italic" }} className="text-orange-500">
              ATPPL
            </em>
          </h2>
          <div className="w-14 h-[3px] bg-orange-500 mx-auto" />
        </Reveal>

        {/* Intro paragraphs */}
        <Reveal className="max-w-4xl mx-auto mb-20 space-y-6">
          <p className="text-[15.5px] leading-[2] text-gray-600">
            <span className="font-medium text-gray-900">
              Aditya Techno Products Pvt. Ltd. (ATPPL)
            </span>{" "}
            is a globally aligned, ISO-certified engineering and EPC solutions
            company delivering high-performance Electrical, MEP, Civil
            Finishing, and Solar EPC projects across India and international
            markets.
          </p>
          <p className="text-[15.5px] leading-[2] text-gray-600">
            Established in 2007 by seasoned engineering professionals, ATPPL
            has evolved into a trusted execution partner for complex
            infrastructure and building services projects. With{" "}
            <span className="font-medium text-gray-900">ISO 9001:2015</span>{" "}
            Quality Management Systems and{" "}
            <span className="font-medium text-gray-900">ISO 45001:2018</span>{" "}
            Occupational Health &amp; Safety standards, we operate with
            structured governance, technical precision, and uncompromising
            safety compliance.
          </p>
        </Reveal>

        {/* ── MAIN 2-COLUMN GRID ── */}
        <div className="grid lg:grid-cols-2 gap-20 items-start">

          {/* LEFT */}
          <div>
            <Reveal>
              <h3
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                className="text-[28px] text-gray-900 mb-8"
              >
                Integrated Engineering &amp;{" "}
                <em style={{ fontStyle: "italic" }} className="text-orange-500">
                  Turnkey Expertise
                </em>
              </h3>

              <ul className="mb-12">
                {capabilities.map((item, i) => (
                  <li
                    key={i}
                    className="cap-item flex items-center gap-5 py-[14px] border-b border-gray-100"
                    style={{ cursor: "default" }}
                  >
                    <span
                      className="cap-bar w-[3px] h-5 bg-orange-500 flex-shrink-0"
                      style={{ transition: "height 0.3s ease" }}
                    />
                    <span
                      className="cap-text text-[14px] text-gray-500"
                      style={{ fontWeight: 400, transition: "color 0.3s ease" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Pull quote */}
            <Reveal delay={80}>
              <div className="border-l-[3px] border-orange-500 pl-6 py-4 bg-orange-50 mb-12">
                <p
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontStyle: "italic",
                    fontWeight: 400,
                  }}
                  className="text-[18px] text-gray-700 leading-relaxed"
                >
                  "Our multidisciplinary team of 35+ engineers and 250+
                  supervisors ensures seamless coordination across all project
                  phases — delivering depth of capability with strong
                  on-ground execution."
                </p>
              </div>
            </Reveal>

            <Reveal delay={130}>
              <h3
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                className="text-[28px] text-gray-900 mb-6"
              >
                Governance, Compliance &amp;{" "}
                <em style={{ fontStyle: "italic" }} className="text-orange-500">
                  Global Credentials
                </em>
              </h3>
              <p className="text-[14.5px] leading-[1.95] text-gray-500 mb-5">
                Registered with leading Government of India bodies including{" "}
                <span className="font-medium text-gray-800">MNRE, MSME, NSIC</span>,
                and active members of recognised industry associations{" "}
                <span className="font-medium text-gray-800">IIA</span> and{" "}
                <span className="font-medium text-gray-800">FIEO</span>.
              </p>
              <p className="text-[14.5px] leading-[1.95] text-gray-500">
                Our international credibility is reinforced through U.S.
                contractor registrations including{" "}
                <span className="font-medium text-gray-800">D-U-N-S (650400935)</span>{" "}
                and{" "}
                <span className="font-medium text-gray-800">N-CAGE (SFP37)</span>{" "}
                — enabling participation in global and government-linked
                opportunities.
              </p>
            </Reveal>
          </div>

          {/* RIGHT — project images */}
          <Reveal delay={180} className="flex flex-col items-center gap-6">
            {[
              { src: "/project-images/airport1.webp", alt: "Airport Infrastructure Project", caption: "Airport Infrastructure" },
              { src: "/project-images/solar1.webp",   alt: "Solar EPC Project",              caption: "Solar EPC Installations" },
            ].map(({ src, alt, caption }, i) => (
              <div
                key={i}
                className="img-wrap relative w-[88%] overflow-hidden"
                style={{ cursor: "default" }}
              >
                <div
                  className="img-frame absolute inset-0 border border-orange-200 z-10 pointer-events-none"
                  style={{ transition: "border-color 0.4s ease" }}
                />
                <img
                  src={src}
                  alt={alt}
                  className="w-full block shadow-md"
                  style={{ transition: "transform 0.6s ease" }}
                />
                <div className="absolute bottom-0 left-0 right-0 px-5 py-3 bg-gradient-to-t from-black/55 to-transparent z-10">
                  <p
                    className="text-[11px] tracking-[0.22em] uppercase text-orange-300"
                    style={{ fontWeight: 500 }}
                  >
                    {caption}
                  </p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>

        {/* ══════════════════════════════════════
            CREDENTIALS GRID
        ══════════════════════════════════════ */}
        <div className="mt-28">
          <Reveal className="text-center mb-12">
            <p
              className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-4 flex items-center justify-center gap-3"
              style={{ fontWeight: 500 }}
            >
              <span className="w-6 h-px bg-orange-400" />
              Trust &amp; Compliance
              <span className="w-6 h-px bg-orange-400" />
            </p>
            <h3
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
              className="text-4xl text-gray-900"
            >
              Internationally{" "}
              <em style={{ fontStyle: "italic" }} className="text-orange-500">
                Recognised
              </em>
            </h3>
            <div className="w-14 h-[3px] bg-orange-500 mx-auto mt-5" />
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 border border-orange-100">
            {credentials.map((c, i) => (
              <Reveal key={i} delay={i * 60}>
                <div
                  className="cred-row flex items-center gap-5 px-8 py-6 border-b border-r border-orange-100"
                  style={{ transition: "background 0.25s ease" }}
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-orange-200 text-orange-500 text-base flex-shrink-0">
                    {c.icon}
                  </div>
                  <div>
                    <div
                      className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-1"
                      style={{ fontWeight: 500 }}
                    >
                      {c.label}
                    </div>
                    <div className="text-[14px] text-gray-700" style={{ fontWeight: 400 }}>
                      {c.value}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            SECTOR EXPERIENCE
        ══════════════════════════════════════ */}
        <div className="mt-28">
          <Reveal className="text-center mb-14">
            <p
              className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-4 flex items-center justify-center gap-3"
              style={{ fontWeight: 500 }}
            >
              <span className="w-6 h-px bg-orange-400" />
              Where We Excel
              <span className="w-6 h-px bg-orange-400" />
            </p>
            <h3
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
              className="text-4xl text-gray-900"
            >
              Leadership &amp;{" "}
              <em style={{ fontStyle: "italic" }} className="text-orange-500">
                Sector Experience
              </em>
            </h3>
            <div className="w-14 h-[3px] bg-orange-500 mx-auto mt-5" />
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sectors.map((sector, i) => (
              <Reveal key={i} delay={i * 55}>
                <div
                  className="sector-tile relative overflow-hidden bg-gray-50 border border-gray-100 px-6 py-7"
                  style={{ transition: "background 0.3s, border-color 0.3s", cursor: "default" }}
                >
                  {/* Orange underline sweep on hover */}
                  <div
                    className="sweep absolute bottom-0 left-0 h-[3px] bg-orange-500 w-0"
                    style={{ transition: "width 0.4s ease" }}
                  />
                  <span className="text-2xl mb-4 block">{sector.icon}</span>
                  <span
                    className="text-[13px] text-gray-600 leading-snug"
                    style={{ fontWeight: 400 }}
                  >
                    {sector.label}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            LEADERSHIP SECTION
        ══════════════════════════════════════ */}
        <div className="mt-28">
          <Reveal className="text-center mb-16">
            <p
              className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-4 flex items-center justify-center gap-3"
              style={{ fontWeight: 500 }}
            >
              <span className="w-6 h-px bg-orange-400" />
              The Minds Behind the Mission
              <span className="w-6 h-px bg-orange-400" />
            </p>
            <h3
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
              className="text-4xl text-gray-900"
            >
              Driving Excellence in{" "}
              <em style={{ fontStyle: "italic" }} className="text-orange-500">
                Engineering &amp; EPC Solutions
              </em>
            </h3>
            <div className="w-14 h-[3px] bg-orange-500 mx-auto mt-5" />
            <p className="mt-6 text-[15px] leading-[1.9] text-gray-500 max-w-3xl mx-auto">
              At Aditya Techno Products Pvt. Ltd., our strength lies in visionary leadership backed by decades of hands-on experience in Electrical and MEP engineering. Our core team brings technical expertise, strategic foresight, and an unwavering commitment to quality across diverse sectors.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-10 items-stretch">

            {/* ── Director 1: Satendra Singh Parihar ── */}
            <Reveal delay={0} className="h-full">
              <div className="h-full flex flex-col bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                {/* Header band */}
                <div className="bg-orange-500 px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold shrink-0"
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                      SP
                    </div>
                    <div>
                      <h4 className="text-white text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Satendra Singh Parihar
                      </h4>
                      <p className="text-orange-100 text-[13px] mt-0.5">Director</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {["MBA", "MNRE", "RTSP Trained", "Ex-Chairman – IIA Delhi"].map((tag) => (
                      <span key={tag} className="bg-white/20 text-white text-[11px] px-3 py-1 rounded-full tracking-wide">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Body */}
                <div className="px-8 py-7 flex-1">
                  <p className="text-[14px] leading-[1.9] text-gray-500 mb-6">
                    With over <span className="font-medium text-gray-800">25+ years of experience</span> (since 1996) in Electrical &amp; MEP project execution, Mr. Parihar has led large-scale engineering initiatives across India. His strategic leadership and deep technical expertise have shaped complex projects across multiple industries.
                  </p>

                  <h5 className="text-[11px] tracking-[0.2em] uppercase text-orange-500 font-semibold mb-4">
                    Key Sector Experience
                  </h5>

                  <ul className="space-y-3">
                    {[
                      { sector: "Hotels & Luxury Resorts", projects: "The Oberoi (Agra), Udaivilas (Udaipur), Four Seasons, Leela Beach (Goa), Fraser Suites (Delhi)" },
                      { sector: "Residential & Townships", projects: "EIH Ltd., AWHO, Air Force Naval Housing Board, Indian Railways WO, Supertech (Noida)" },
                      { sector: "Hospitals", projects: "Apollo Hospital (Sarita Vihar, New Delhi)" },
                      { sector: "Airports", projects: "Trivandrum International Airport, Chitrakoot Airport" },
                      { sector: "Expo Centre", projects: "IICC Dwarka, New Delhi" },
                      { sector: "Retail & Commercial", projects: "Future Group, Aditya Birla Group, Celio Group (150+ stores)" },
                      { sector: "Telecom Infrastructure", projects: "BSNL (100+ sites), IDIA (80+ sites), Airtel (50+ sites)" },
                    ].map(({ sector, projects }) => (
                      <li key={sector} className="flex gap-3 text-[13.5px]">
                        <span className="w-[3px] h-auto bg-orange-400 rounded-full shrink-0 mt-1" />
                        <span>
                          <span className="font-medium text-gray-800">{sector}: </span>
                          <span className="text-gray-500">{projects}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* ── Director 2: Kailash Chandra ── */}
            <Reveal delay={100} className="h-full">
              <div className="h-full flex flex-col bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                {/* Header band */}
                <div className="bg-gray-900 px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white text-2xl font-bold shrink-0"
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                      KC
                    </div>
                    <div>
                      <h4 className="text-white text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Kailash Chandra
                      </h4>
                      <p className="text-gray-400 text-[13px] mt-0.5">Director</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {["B.Tech (Electronics)", "MEP Projects Since 2005"].map((tag) => (
                      <span key={tag} className="bg-white/10 text-gray-300 text-[11px] px-3 py-1 rounded-full tracking-wide">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Body */}
                <div className="px-8 py-7 flex-1">
                  <p className="text-[14px] leading-[1.9] text-gray-500 mb-6">
                    With experience since <span className="font-medium text-gray-800">2005 in MEP Projects</span>, Mr. Kailash Chandra specializes in advanced electrical systems, project coordination, and execution management across commercial and institutional developments.
                  </p>

                  <h5 className="text-[11px] tracking-[0.2em] uppercase text-orange-500 font-semibold mb-4">
                    Key Sector Experience
                  </h5>

                  <ul className="space-y-3">
                    {[
                      { sector: "Hospitality", projects: "Ashoka Hotel (New Delhi), Fraser Suites" },
                      { sector: "Residential Housing", projects: "AWHO, Air Force Naval Housing Board, Indian Railways WO" },
                      { sector: "Hospitals", projects: "Apollo Hospital (New Delhi)" },
                      { sector: "Industrial Plants", projects: "ITC (Haridwar), Airport Authority of Delhi, Mapex, Sarvottam Baddi" },
                      { sector: "Corporate Offices", projects: "Oil India Ltd., AICTE, Chandrlok Building, Maruti Udyog Corporate Office" },
                      { sector: "Telecom Projects", projects: "Ericsson India (BSNL & IDIA), Airtel deployments" },
                      { sector: "Retail Projects", projects: "150+ stores across major national brands" },
                    ].map(({ sector, projects }) => (
                      <li key={sector} className="flex gap-3 text-[13.5px]">
                        <span className="w-[3px] h-auto bg-gray-300 rounded-full shrink-0 mt-1" />
                        <span>
                          <span className="font-medium text-gray-800">{sector}: </span>
                          <span className="text-gray-500">{projects}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

          </div>
        </div>

        {/* ══════════════════════════════════════
            COMMITMENT BOX
        ══════════════════════════════════════ */}
        <Reveal className="mt-28">
          <div className="relative text-center max-w-4xl mx-auto px-10 py-16 bg-orange-50 border border-orange-100">

            {/* Corner bracket accents */}
            <span className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-orange-400" style={{ margin: 8 }} />
            <span className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-orange-400" style={{ margin: 8 }} />
            <span className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-orange-400" style={{ margin: 8 }} />
            <span className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-orange-400" style={{ margin: 8 }} />

            <p
              className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-5 flex items-center justify-center gap-3"
              style={{ fontWeight: 500 }}
            >
              <span className="w-6 h-px bg-orange-400" />
              Our Commitment
              <span className="w-6 h-px bg-orange-400" />
            </p>

            <h3
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
              className="text-4xl text-gray-900 mb-8"
            >
              Built on{" "}
              <em style={{ fontStyle: "italic" }} className="text-orange-500">
                Trust &amp; Performance
              </em>
            </h3>

            <p className="text-[15px] leading-[2] text-gray-600 mb-8">
              We combine engineering excellence with disciplined project
              management to deliver on-time execution, cost transparency,
              stringent quality assurance, safety-first operations, and
              long-term client partnerships.
            </p>

            <p
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 400,
              }}
              className="text-[22px] text-gray-800 leading-relaxed"
            >
              "At ATPPL, we don't just execute projects — we engineer
              reliability, performance, and lasting value."
            </p>
          </div>
        </Reveal>

      </section>

      {/* ══════════════════════════════════════
          FOOTER STRIP
      ══════════════════════════════════════ */}
      <div className="relative overflow-hidden">
        <div
          className="bg-orange-600 text-white text-center py-7 tracking-[0.25em] text-[13px] uppercase"
          style={{ fontWeight: 600 }}
        >
          Engineering Reliability &amp; Value, Globally.
        </div>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)",
            animation: "shineSweep 4s ease-in-out infinite",
          }}
        />
      </div>
    </div>
  );
}